import {DiffFileHeader} from '@github-ui/diff-file-header'
import type React from 'react'
import {memo, useCallback, useId, useMemo, useState} from 'react'
import {
  CommentSubjectType,
  type DiffEntry,
  type DiffLineKey,
  type DiffUser,
  type FileDiffReference,
  type RichDiff as RichDiffType,
} from '../types'
import type {Thread} from '../conversation-types'
import {StartThreadComment} from './conversations/StartThreadComment'
import {RichDiff, type RichDiffProps} from './RichDiff'
import {DiffLines} from './DiffLines'
import styles from './Diff.module.css'
import {SubmoduleDiff} from './SubmoduleDiff'
import {useCopilotChatReference} from '../hooks/use-copilot-chat-reference'
import type {Repository} from '@github-ui/current-repository'
import {Blankslate} from '@primer/react/experimental'
import {AlertIcon, InfoIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {FileMarkers} from './conversations/FileMarkers'
import type {InjectedContextlinesSliceOpts} from '../stores/slices/injected-context-lines'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {DiffStoreProvider} from '../stores/DiffStore'
import {useCreateReviewCommentMutation} from '@github-ui/pull-requests/use-create-review-comment-mutation'
import {
  useComparisonInfo,
  useIsSingleFileMode,
  usePullRequestBasePath,
  useSelectedRange,
} from '@github-ui/pull-requests/pull-request-store-selectors'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {ReviewCommentFactory} from '../helpers/review-comment-factory'
import {DiffFeature} from '@github-ui/diffs/types'
import {FileLevelCode} from '../stores/slices/move-new-comment'
import {buildStoredCommentMap, diffLineKey} from '../stores/slices/helpers/diff-lines-slice-helpers'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {processLineForHiddenUnicode} from '../helpers/process-diffline-for-hidden-unicode'

const defaultHiddenFeatures = new Set<DiffFeature>()
const fileDiffLineKey = `b:0-l:null-r:null` as DiffLineKey

function getSingleFileModeMessage(comparisonView: ReturnType<typeof useComparisonInfo>['comparisonView']): string {
  switch (comparisonView) {
    case 'COMMIT':
      return 'Due to the large number of changes in the selected commit, only one file is being shown at a time.'
    case 'RANGE':
      return 'Due to the large number of changes in the selected commit range, only one file is being shown at a time.'
    default:
      return 'Due to the large number of changes in this pull request, only one file is being shown at a time.'
  }
}

export interface RichDiffState {
  richDiff?: RichDiffType
  showRichDiff: boolean
  onToggleRichDiff: (showRichDiff: boolean) => void
  queryConfig?: RichDiffProps['queryConfig']
}

/**
 * DiffProps extends the Diff data type by adding additional,
 * more UI-related properties.
 */
export interface DiffProps extends DiffEntry {
  addInjectedContextLines: InjectedContextlinesSliceOpts['addInjectedContextLines']
  addFileCommentButtonRef?: React.RefObject<HTMLButtonElement | null>
  collapsed: boolean
  diffManuallyExpanded: boolean
  estimatedDiffHeight?: number // in pixels, for content visibility optimization
  fileComments: Thread[]
  headerStickyOffset?: number
  hiddenFeatures?: Set<DiffFeature>
  isAddingFileComment?: boolean
  isSelected?: boolean
  rightSideContent: React.ReactNode | null
  setIsAddingFileComment: (value: boolean) => void
  pullRequestGlobalRelayId?: string
  leftSideContent: React.ReactNode | null
  onToggleCollapse: (event: React.MouseEvent, collapsed: boolean) => void
  currentUser: DiffUser
  repository: Pick<Repository, 'id' | 'name' | 'ownerLogin'>
}

export interface DiffContextProps extends DiffProps {
  canExpandOrCollapseLines: boolean
  hasExpandedAllRanges: boolean
  expandAllContextLines: () => void
  loadDiff: () => Promise<void>
  richDiffState?: RichDiffState
  ref?: React.Ref<HTMLDivElement>
}

function DiffUnmemoized({
  ref,
  addFileCommentButtonRef,
  collapsed,
  currentUser,
  diffLines,
  diffManuallyExpanded,
  diffSize,
  helpUrl,
  estimatedDiffHeight = 0,
  fileComments,
  headerStickyOffset = 0,
  hiddenFeatures = defaultHiddenFeatures,
  isAddingFileComment,
  isBinary,
  isSelected = false,
  isSubmodule,
  isTooBig,
  leftSideContent,
  linesAdded,
  linesChanged,
  linesDeleted,
  newTreeEntry,
  newCommitOid,
  objectId,
  oldTreeEntry,
  oldCommitOid,
  onToggleCollapse,
  path,
  pathDigest,
  pullRequestGlobalRelayId,
  repository,
  richDiffState,
  rightSideContent,
  setIsAddingFileComment,
  hasExpandedAllRanges,
  expandAllContextLines,
  canExpandOrCollapseLines,
  status,
  submodule,
  truncatedReason,
  addInjectedContextLines,
  loadDiff,
}: DiffContextProps) {
  const showSubmodule = isSubmodule && !!submodule
  const showDiffLines = !showSubmodule && !richDiffState?.showRichDiff
  const [linesManuallyUnhidden, setLinesManuallyUnhidden] = useState(diffManuallyExpanded)
  const isInSingleFileMode = useIsSingleFileMode()
  const {comparisonView} = useComparisonInfo()
  const basePath = usePullRequestBasePath()
  const [showHiddenUnicodeCharacters, setShowHiddenUnicodeCharacters] = useState(false)

  // Set .key values on each diffLine for the DiffLines and children components to use
  // (e.g. td.data-* attributes, Zustand store selectors, etc..)
  const diffLinesWithKeys = useMemo(() => {
    return diffLines.map(line => {
      const diffLine = processLineForHiddenUnicode(line, showHiddenUnicodeCharacters)
      return {
        ...diffLine,
        key: diffLineKey(line),
      }
    })
  }, [diffLines, showHiddenUnicodeCharacters])

  const storedCommentsMap = useMemo(() => buildStoredCommentMap(diffLines, path, basePath), [diffLines, path, basePath])
  const copilotChatReference: FileDiffReference | undefined = useCopilotChatReference({
    isBinary,
    isSubmodule,
    path,
    status,
    repository,
    newCommitOid,
    newTreeEntry,
    oldCommitOid,
    oldTreeEntry,
    pathDigest,
    hasCopilotAccess: currentUser.hasCopilotAccess,
  })

  const onHandleLoadDiff = useCallback(async () => {
    await loadDiff()

    setLinesManuallyUnhidden(true)
  }, [loadDiff])

  // TODO: remove this use of useSelectedRange when we remove :cotd_ui Feature Flag
  const {baseOid: startOid, headOid: endOid} = useSelectedRange()
  const commentOutsideOfTheDiffEnabled = useFeatureFlag('cotd_ui')
  const isContentVisibilityOnDiffEnabled = isFeatureEnabled('pr_simple_diffs')
  const {effectiveRange: effectiveDiffRange} = useComparisonInfo()
  const {mutate: createReviewCommentMutation} = useCreateReviewCommentMutation()
  const handleBeginAddFileComment = useCallback(() => {
    setIsAddingFileComment(true)
  }, [setIsAddingFileComment])

  const startNewConversationForLineOrFileMap = useMemo(() => {
    return {
      [FileLevelCode]: {startNewComment: handleBeginAddFileComment, diffLineKey: fileDiffLineKey},
    }
  }, [handleBeginAddFileComment])

  const handleAddFileComment = ({
    onCompleted,
    onError,
    submitBatch,
    text,
  }: {
    onCompleted?: (threadId: string, commentDatabaseId: number | undefined | null) => void
    onError: (error: Error) => void
    submitBatch?: boolean | undefined
    text: string
  }) => {
    if (commentOutsideOfTheDiffEnabled) {
      const requestData = new ReviewCommentFactory({
        filePath: path,
        effectiveDiffRange,
        submitBatch,
        text,
      }).fileRequestData()
      createReviewCommentMutation(requestData, {
        onError,
        onSuccess: data => {
          onCompleted?.(data.thread.id, data.comment.databaseId)
          setIsAddingFileComment(false)
        },
      })
    } else {
      createReviewCommentMutation(
        {
          text,
          path,
          submitBatch,
          subjectType: CommentSubjectType.File,
          comparisonEndOid: endOid,
          comparisonStartOid: startOid,
        },
        {
          onError,
          onSuccess: data => {
            onCompleted?.(data.thread.id, data.comment.databaseId)
            setIsAddingFileComment(false)
          },
        },
      )
    }
  }

  const onCancelComment = () => {
    setIsAddingFileComment(false)
    addFileCommentButtonRef?.current?.focus()
  }

  const viewerData = useMemo(() => {
    return {
      avatarUrl: currentUser?.avatarURL ?? '',
      diffViewPreference: currentUser?.splitPreference,
      login: currentUser?.login ?? '',
      lineSpacingPreference: currentUser?.lineSpacing,
      shouldDisplayComments: currentUser?.shouldDisplayComments,
      shouldDisplayEditOptions: currentUser?.shouldDisplayEditOptions,
      tabSizePreference: currentUser?.tabSize ?? 4,
      viewerCanComment: currentUser?.canComment,
      viewerCanApplySuggestion: currentUser?.canApplySuggestion,
    }
  }, [
    currentUser.avatarURL,
    currentUser.canApplySuggestion,
    currentUser.canComment,
    currentUser.lineSpacing,
    currentUser.login,
    currentUser.shouldDisplayComments,
    currentUser.shouldDisplayEditOptions,
    currentUser.splitPreference,
    currentUser.tabSize,
  ])

  const id = useId()
  const headingId = `heading-${id}`

  const isSplitView = useMemo(() => {
    const searchParams = new URLSearchParams(ssrSafeWindow?.location.search)
    const diffSearchParams = searchParams.get('diff')
    if (diffSearchParams === 'split') return true

    return currentUser.splitPreference === 'split'
  }, [currentUser.splitPreference])

  const fileLineCount = newTreeEntry?.lineCount ?? oldTreeEntry?.lineCount ?? 0

  // Sets the table's aria-label to be the accessible name of the `region`,
  // prefixed with `Diff for:`.
  // This text should be kept in sync with the heading text in
  // `packages/diff-file-header/DiffFileHeader.tsx`.
  const tableAriaLabel = useMemo(() => {
    const oldPath = oldTreeEntry?.path
    const newPath = newTreeEntry?.path

    if (!oldPath && !newPath) return undefined

    let fileName
    if (status === 'RENAMED') {
      fileName = `${oldPath} renamed to ${newPath}`
    } else if (status === 'DELETED' || status === 'REMOVED') {
      fileName = oldPath
    } else {
      fileName = newPath
    }
    return `Diff for: ${fileName}`
  }, [status, oldTreeEntry?.path, newTreeEntry?.path])

  const diffEntryData = useMemo(
    () => ({
      diffLines: diffLinesWithKeys,
      isBinary,
      isTooBig,
      linesChanged,
      newTreeEntry,
      newCommitOid,
      objectId,
      oldTreeEntry,
      oldCommitOid,
      path,
      pathDigest,
      status,
      truncatedReason,
    }),
    [
      diffLinesWithKeys,
      isBinary,
      isTooBig,
      linesChanged,
      newCommitOid,
      newTreeEntry,
      objectId,
      oldCommitOid,
      oldTreeEntry,
      path,
      pathDigest,
      status,
      truncatedReason,
    ],
  )

  return (
    <DiffStoreProvider
      addInjectedContextLines={addInjectedContextLines}
      diffAnchor={`diff-${pathDigest}`}
      diffLines={diffLinesWithKeys}
      fileLineCount={fileLineCount}
      path={path}
      startNewConversationForLineOrFileMap={startNewConversationForLineOrFileMap}
      hiddenFeatures={hiddenFeatures}
      isSplitView={isSplitView}
      oldTreeEntry={oldTreeEntry}
      newTreeEntry={newTreeEntry}
      storedCommentsMap={storedCommentsMap}
    >
      <div
        data-estimated-height={estimatedDiffHeight}
        role="region"
        ref={ref}
        aria-labelledby={headingId}
        id={`diff-${pathDigest}`}
        className={clsx(styles.diffTargetable, styles.diff)}
        data-targeted={isSelected}
        key={`${pathDigest}_${diffLinesWithKeys.length}`}
        style={
          isContentVisibilityOnDiffEnabled
            ? {contentVisibility: 'auto', containIntrinsicSize: `auto ${estimatedDiffHeight}px`}
            : undefined
        }
      >
        <div
          className={styles.diffHeaderWrapper}
          style={{
            '--header-sticky-offset': `${headerStickyOffset}px`,
          }}
        >
          <DiffFileHeader
            headingId={headingId}
            hiddenFeatures={hiddenFeatures}
            areLinesExpanded={hasExpandedAllRanges}
            canExpandOrCollapseLines={canExpandOrCollapseLines}
            fileLinkHref={`#diff-${pathDigest}`}
            isCollapsed={collapsed}
            isBinary={isBinary}
            size={diffSize}
            canToggleRichDiff={richDiffState?.richDiff?.canToggleRichDiff}
            linesAdded={linesAdded}
            linesChanged={linesChanged}
            linesDeleted={linesDeleted}
            newMode={newTreeEntry?.mode}
            newPath={newTreeEntry?.path}
            oldMode={oldTreeEntry?.mode}
            oldPath={oldTreeEntry?.path}
            patchStatus={status}
            path={path}
            onToggleExpandAllLines={expandAllContextLines}
            onToggleFileCollapsed={event => onToggleCollapse(event, !collapsed)}
            onToggleDiffDisplay={richDiffState?.onToggleRichDiff}
            additionalLeftSideContent={leftSideContent}
            rightSideContent={rightSideContent}
            showRichDiff={richDiffState?.showRichDiff}
            isInSingleFileMode={isInSingleFileMode}
          />
        </div>
        {!collapsed ? (
          <div className="border position-relative rounded-bottom-2">
            <>
              {fileComments.length > 0 && <FileMarkers conversationListThreads={fileComments} filePath={path} />}
              {isAddingFileComment && (
                <div
                  className={clsx(
                    'border rounded-2 color-border-default m-1 rounded-2 bgColor-default',
                    styles.diffAddFileThread,
                  )}
                >
                  <StartThreadComment
                    batchingEnabled
                    filePath={path}
                    fileLevelComment
                    isLeftSide={undefined}
                    viewerData={viewerData}
                    onAddComment={handleAddFileComment}
                    onClose={onCancelComment}
                  />
                </div>
              )}
            </>
            {showSubmodule && <SubmoduleDiff submodule={submodule} />}

            {richDiffState?.showRichDiff && (
              <RichDiff queryConfig={richDiffState.queryConfig} initialData={richDiffState.richDiff} />
            )}

            {showDiffLines && (
              <DiffLines
                hasExpandedAllRanges={hasExpandedAllRanges}
                copilotChatReference={copilotChatReference}
                diffEntryData={diffEntryData}
                baseHelpUrl={helpUrl}
                subjectId={pullRequestGlobalRelayId || ''}
                viewerData={viewerData}
                isSplitView={isSplitView}
                diffLinesManuallyUnhidden={linesManuallyUnhidden}
                handleSetShowHiddenUnicodeCharacters={setShowHiddenUnicodeCharacters}
                onHandleLoadDiff={onHandleLoadDiff}
                showHiddenUnicodeCharacters={showHiddenUnicodeCharacters}
                tableAriaLabel={tableAriaLabel}
              />
            )}
          </div>
        ) : null}
      </div>
      {isInSingleFileMode && (
        <div className={styles.diffFooter}>
          <InfoIcon size={16} aria-hidden="true" />
          <span className="text-center">{getSingleFileModeMessage(comparisonView)}</span>
        </div>
      )}
    </DiffStoreProvider>
  )
}

type DiffErrorFallbackProps = Pick<
  DiffEntry,
  'path' | 'pathDigest' | 'linesAdded' | 'linesChanged' | 'linesDeleted' | 'status' | 'newTreeEntry' | 'oldTreeEntry'
>

export function DiffErrorFallback({
  path,
  pathDigest,
  linesAdded,
  linesChanged,
  linesDeleted,
  oldTreeEntry,
  newTreeEntry,
  status,
}: DiffErrorFallbackProps) {
  const id = useId()
  const headingId = `heading-${id}`

  const [isCollapsed, setIsCollapsed] = useState(false)

  return (
    <div
      role="region"
      aria-labelledby={headingId}
      id={`diff-${pathDigest}`}
      className={clsx(styles.diffTargetable, styles.diff)}
      key={`${pathDigest}_error`}
    >
      <div className={styles.diffHeaderWrapper}>
        <DiffFileHeader
          headingId={headingId}
          isCollapsed={isCollapsed}
          isBinary={false}
          linesAdded={linesAdded}
          linesChanged={linesChanged}
          linesDeleted={linesDeleted}
          newMode={newTreeEntry?.mode}
          newPath={newTreeEntry?.path}
          oldMode={oldTreeEntry?.mode}
          oldPath={oldTreeEntry?.path}
          patchStatus={status}
          path={path}
          onToggleFileCollapsed={() => setIsCollapsed(!isCollapsed)}
        />
      </div>
      {!isCollapsed && (
        <div className="border position-relative rounded-bottom-2">
          <Blankslate>
            <Blankslate.Visual>
              <AlertIcon size="medium" className="fgColor-muted" />
            </Blankslate.Visual>
            <Blankslate.Heading as="h4">There was an issue loading this file</Blankslate.Heading>
            <Blankslate.Description>
              {' '}
              Try refreshing the page or if the problem persists{' '}
              <a className="fgColor-muted" href="https://support.github.com/">
                <u>contact support</u>
              </a>
              .
            </Blankslate.Description>
          </Blankslate>
        </div>
      )}
    </div>
  )
}

/**
 * Represents a single file diff.
 *
 * @params DiffContextProps
 * @returns Diff
 */

export const Diff = memo(DiffUnmemoized)

try{ DiffUnmemoized.displayName ||= 'DiffUnmemoized' } catch {}
try{ DiffErrorFallback.displayName ||= 'DiffErrorFallback' } catch {}
try{ Diff.displayName ||= 'Diff' } catch {}